import React, { useCallback, useEffect, useState } from 'react'
import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusSquareOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { Button, Card, Drawer, Form, Space, Table, Tag, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'

import { useNavigate, useParams } from 'react-router-dom'
import { useAppContext } from '@/components/AppContext'
import withDefaultLayout from '@/hoc/WithDefaultLayout'
import { Shrine } from '@a4b/api/src/modules/Temple/types'

import {
  formatCardCreatePayload,
  formatCardUpdatePayload,
  parseCardToFormFields,
} from '../ManageEventCalendar/utils/helper'
import CardContentsDrawer from './components/CardContentsDrawer'
import CardForm from './components/CardForm'

type CardRow = Shrine.Card

const ManageCards: React.FC = () => {
  const { networkInstance } = useAppContext()
  const navigate = useNavigate()
  const { eventCalendarId } = useParams<{ eventCalendarId: string }>()
  const [form] = Form.useForm()

  const [loadingCards, setLoadingCards] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [cards, setCards] = useState<CardRow[]>([])

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingCard, setEditingCard] = useState<CardRow | null>(null)

  const [contentsDrawerOpen, setContentsDrawerOpen] = useState(false)
  const [selectedCard, setSelectedCard] = useState<CardRow | null>(null)

  const fetchCards = useCallback(async () => {
    if (!eventCalendarId) return
    setLoadingCards(true)
    try {
      const response =
        await networkInstance.clientWithHeaders.shrineApi.getCards(
          eventCalendarId,
        )
      const list = response?.data?.data ?? []
      setCards(list)
    } catch (err) {
      message.error('Failed to fetch cards')
      console.error(err)
    } finally {
      setLoadingCards(false)
    }
  }, [networkInstance, eventCalendarId])

  useEffect(() => {
    fetchCards()
  }, [fetchCards])

  const openCreate = useCallback(() => {
    console.log('Opening create drawer')
    setEditingCard(null)
    form.resetFields()
    form.setFieldsValue({ active: true, position: 0 })
    setDrawerOpen(true)
  }, [form])

  const openEdit = useCallback(
    (record: CardRow) => {
      console.log('Opening edit drawer for card:', record)
      setEditingCard(record)
      form.setFieldsValue(parseCardToFormFields(record))
      setDrawerOpen(true)
    },
    [form],
  )

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false)
    setEditingCard(null)
    form.resetFields()
  }, [form])

  const handleSubmit = useCallback(
    async (values: any) => {
      if (!eventCalendarId) return
      if (submitting) return
      setSubmitting(true)
      try {
        if (editingCard?.id) {
          const payload = formatCardUpdatePayload(
            values,
            eventCalendarId,
            editingCard.id,
          )
          await networkInstance.clientWithHeaders.shrineApi.updateCard(
            eventCalendarId,
            editingCard.id,
            payload,
          )
          message.success('Card updated')
        } else {
          const payload = formatCardCreatePayload(values, eventCalendarId)
          await networkInstance.clientWithHeaders.shrineApi.createCard(
            eventCalendarId,
            payload,
          )
          message.success('Card created')
        }

        closeDrawer()
        fetchCards()
      } catch (err) {
        message.error('Failed to save card')
        console.error(err)
      } finally {
        setSubmitting(false)
      }
    },
    [
      editingCard,
      networkInstance,
      eventCalendarId,
      closeDrawer,
      fetchCards,
      submitting,
    ],
  )

  const openCardContents = useCallback((record: CardRow) => {
    setSelectedCard(record)
    setContentsDrawerOpen(true)
  }, [])

  const closeCardContentsDrawer = useCallback(() => {
    setContentsDrawerOpen(false)
    setSelectedCard(null)
  }, [])

  const columns: ColumnsType<CardRow> = [
    {
      title: 'S.No',
      key: 'index',
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Card ID',
      dataIndex: 'id',
      key: 'id',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Card Type',
      dataIndex: 'card_type',
      key: 'card_type',
      width: 150,
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      width: 90,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 90,
      render: val =>
        val ? <Tag color='green'>Yes</Tag> : <Tag color='red'>No</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size='small'>
          {!['upcoming_tithi', 'panchang'].includes(
            String(record.card_type ?? '').toLowerCase(),
          ) && (
              <Button
                type='link'
                icon={<UnorderedListOutlined />}
                onClick={() => openCardContents(record)}
              >
                Configure Contents
              </Button>
            )}
          <Button
            type='link'
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <Card
      className='card-flex-column-grow'
      title={
        <Space>
          <Button
            type='text'
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/shraddha-shorts/event-calendar')}
          />
          <span>Manage Cards - Event Calendar: {eventCalendarId}</span>
        </Space>
      }
      extra={
        <Button
          type='primary'
          icon={<PlusSquareOutlined />}
          onClick={openCreate}
        >
          Add Card
        </Button>
      }
    >
      <div className='full-size-flex-column'>
        <Table<CardRow>
          rowKey='id'
          loading={loadingCards}
          columns={columns}
          dataSource={cards}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 900 }}
        />

        <Drawer
          title={editingCard ? 'Edit Card' : 'Create Card'}
          width={520}
          visible={drawerOpen}
          onClose={closeDrawer}
          destroyOnClose
        >
          <CardForm
            form={form}
            onFinish={handleSubmit}
            onCancel={closeDrawer}
            isEditing={!!editingCard}
            submitting={submitting}
          />
        </Drawer>

        {contentsDrawerOpen && selectedCard && eventCalendarId && (
          <CardContentsDrawer
            visible={contentsDrawerOpen}
            onClose={closeCardContentsDrawer}
            card={selectedCard}
            eventCalendarId={eventCalendarId}
          />
        )}
      </div>
    </Card>
  )
}

export default withDefaultLayout(ManageCards)
